/**
 * CUSTOMER-R4.15-K11 — when to restore source scroll vs start a route at the top.
 * Session-scoped Home scroll. Not a global “always remember”.
 */
export const NavigationRestorationPolicy = {
  sessionScopedHomeScroll: true,
  restoreOnBack: true,
  restoreHomeOnReturnFrom: [
    "category",
    "search",
    "product",
    "new",
    "promotions",
    "brands",
    "brand",
    "countries",
    "country",
    "dropshipping",
    "profile",
    "cart",
  ],
  freshHomeFrom: [
    "checkout",
    "success",
    "orders",
    "order-detail",
    "messages",
    "invite-friends",
    "engagement-hub",
  ],
} as const;

export type StoreV2RouteName = string;

const FRESH_HOME = new Set<string>(NavigationRestorationPolicy.freshHomeFrom);
const RESTORE_HOME = new Set<string>(NavigationRestorationPolicy.restoreHomeOnReturnFrom);

export function shouldRestoreHomeScroll(fromRouteName: StoreV2RouteName): boolean {
  const name = String(fromRouteName ?? "").trim();
  if (!name || name === "home") return false;
  if (FRESH_HOME.has(name)) return false;
  return RESTORE_HOME.has(name);
}

export function shouldStartFreshOnHome(fromRouteName: StoreV2RouteName): boolean {
  return FRESH_HOME.has(String(fromRouteName ?? "").trim());
}

export function shouldRestoreBackScroll(toRouteName: StoreV2RouteName): boolean {
  const name = String(toRouteName ?? "").trim();
  if (!name) return false;
  return !FRESH_HOME.has(name);
}

const HOME_SCROLL_SESSION_KEY = "asiastyle.store-v2.home-scroll.session.v1";

export function readSessionHomeScroll(): number {
  try {
    const raw = window.sessionStorage.getItem(HOME_SCROLL_SESSION_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

export function writeSessionHomeScroll(top: number): void {
  const n = Number.isFinite(top) ? Math.max(0, Math.floor(top)) : 0;
  try {
    if (n <= 0) window.sessionStorage.removeItem(HOME_SCROLL_SESSION_KEY);
    else window.sessionStorage.setItem(HOME_SCROLL_SESSION_KEY, String(n));
  } catch {
    /* private mode */
  }
}

export function clearSessionHomeScroll(): void {
  writeSessionHomeScroll(0);
}
